import { useEffect, useRef, useState } from "react";
import { usePref } from "../../ui/prefs.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sceneWords, shotRerun, type ProjectView, type ShotView } from "@joogoos/contracts";
import { SceneWords, sceneLabel } from "./SceneWords.js";
import { api } from "../../api.js";
import { Icon } from "../../ui/icons.js";

/**
 * The editor (docs/03): a small NLE. Program monitor on a dark stage with its own transport, an
 * inspector bound to the selection, and a timeline across the bottom with a ruler that scrubs, a
 * playhead that follows playback, zoom with a scroll bar, and a track per lane. Trimming and
 * reordering arrive with the composition API; today the timeline selects and seeks.
 */
export function Editor({ project: planned }: { project: ProjectView }) {
  // Newest first, so the first finished one is the latest cut; reading them oldest first played a post's first render.
  const render = planned.renders.find((r) => r.status === "succeeded" && r.output);
  // The timeline is the cut that plays, not the plan: a spoken shot runs on until its words are said, and the last one
  // holds, so a 20-second plan was a 24-second video whose playhead ran off the end (2026-09-26). A shot the cut does
  // not have (made again since) keeps its planned length.
  const project = { ...planned, shots: planned.shots.map((s) => ({ ...s, durationMs: render?.shots.find((c) => c.id === s.id)?.durationMs ?? s.durationMs })) };
  const [selId, setSel] = useState<string | null>(project.shots[0]?.id ?? null);
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(false);
  // Remembered across posts and visits: somebody who turned the sound off in an office does not want it back on
  // the next video they open (2026-09-25).
  const [muted, setMuted] = usePref("editor.muted", false);
  const [zoom, setZoom] = useState(1);
  const video = useRef<HTMLVideoElement>(null);
  const monitor = useRef<HTMLDivElement>(null);
  const shots = project.shots;
  const total = shots.reduce((n, s) => n + s.durationMs, 0) / 1000 || 15;
  const selected = shots.find((s) => s.id === selId) ?? null;
  const starts = shots.reduce<number[]>((acc, s, i) => [...acc, i ? acc[i - 1]! + shots[i - 1]!.durationMs / 1000 : 0], []);
  const shotAt = (t: number) => shots[Math.max(0, starts.findLastIndex((x) => x <= t + 0.001))];

  function seek(seconds: number) {
    const t = Math.max(0, Math.min(total, seconds));
    setAt(t);
    if (video.current) video.current.currentTime = t;
  }
  const toggle = () => {
    const v = video.current;
    if (!v) return;
    // `play()` hands back a promise that rejects when the browser refuses to start; some have handed back nothing.
    if (v.paused) void Promise.resolve(v.play()).catch(() => {}); else v.pause();
  };
  const step = (dir: 1 | -1) => {
    // To the start of the next shot, or of this one (the previous one when already at its start).
    const i = starts.findLastIndex((x) => x <= at + 0.001);
    const target = dir === 1 ? starts[i + 1] ?? total : at - (starts[i] ?? 0) > 0.3 ? starts[i] ?? 0 : starts[Math.max(0, i - 1)] ?? 0;
    seek(target);
  };

  // Whether the preview is full screen, read from the browser: Esc leaves it without asking us.
  const [full, setFull] = useState(false);
  useEffect(() => {
    const onChange = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => { if (video.current) video.current.muted = muted; }, [muted, render?.output?.url]);
  // While it plays, the inspector follows the picture.
  useEffect(() => { if (playing) { const s = shotAt(at); if (s && s.id !== selId) setSel(s.id); } }, [at, playing]); // eslint-disable-line react-hooks/exhaustive-deps
  // Space, the arrows and M, as every player has them. Never while typing, and never with a modifier held.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey || (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable))) return;
      if (e.key === " ") { e.preventDefault(); toggle(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); seek(at + 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); seek(at - 1); }
      else if (e.key === "m" || e.key === "M") setMuted(!muted);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="p-4 flex flex-col gap-3 md:h-full min-h-0">
      {/* On a phone the picture and the shot's details stack, and the page scrolls, rather than sharing 340 px of height (03 §on a phone). */}
      <div className="flex flex-col md:flex-row gap-3.5 md:h-[340px] shrink-0">
        <div ref={monitor} className="h-[300px] md:h-auto md:flex-1 bg-stage rounded-xl flex flex-col relative min-w-0 overflow-hidden">
          <span className="absolute top-2.5 left-3 text-[10px] font-bold uppercase tracking-[.06em] text-muted z-10">
            Program · preview
          </span>
          {/* The picture fills this box and keeps its shape. A grid cell sized by its content let a 1080×1920 video
              draw 1,220 px tall inside a 300 px box, so only a white slice of it showed (dev box, 2026-09-25). */}
          <div className="flex-1 min-h-0 relative">
            {render?.output ? (
              <video
                ref={video}
                src={render.output.url}
                playsInline
                muted={muted}
                onClick={toggle}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)}
                className="absolute inset-0 w-full h-full object-contain p-2 cursor-pointer"
              />
            ) : (
              <p className="absolute inset-0 grid place-items-center text-faint text-sm">No render yet.</p>
            )}
          </div>
          {render?.output ? (
            <Transport
              at={at} total={total} playing={playing} muted={muted}
              onToggle={toggle} onSeek={seek} onStep={step} onMute={() => setMuted(!muted)}
              full={full}
              onFull={() => {
                // The same button takes the preview out of full screen as into it; Esc was the only way out (2026-09-26).
                const done = document.fullscreenElement ? document.exitFullscreen?.() : monitor.current?.requestFullscreen?.();
                void (done ?? Promise.resolve()).catch(() => {});
              }}
            />
          ) : null}
        </div>
        <Inspector project={project} shot={selected} plannedMs={planned.shots.find((s) => s.id === selected?.id)?.durationMs} />
      </div>

      <div className="min-h-[220px] md:min-h-0 md:flex-1 bg-deck border border-ink rounded-xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-2.5 px-2.5 py-1.5 bg-deck-2 border-b border-ink text-[#c9c3bb]">
          <span className="text-[11px]">Timeline</span>
          <span className="w-px h-4 bg-deck-line" />
          <span className="hidden sm:inline text-[11px] text-[#8a827a]">click a clip to go to it · drag the ruler to scrub · space plays</span>
          <span className="ml-auto flex items-center gap-1">
            <button onClick={() => setZoom(Math.max(1, zoom / 2))} disabled={zoom <= 1} title="Zoom out" aria-label="Zoom out" className="p-1 rounded hover:bg-ink disabled:opacity-40"><Icon name="zoomOut" size={13} /></button>
            <span className="text-[10.5px] tabular-nums w-7 text-center">{zoom}×</span>
            <button onClick={() => setZoom(Math.min(8, zoom * 2))} disabled={zoom >= 8} title="Zoom in" aria-label="Zoom in" className="p-1 rounded hover:bg-ink disabled:opacity-40"><Icon name="zoomIn" size={13} /></button>
          </span>
          <span className="tabular-nums text-xs text-white bg-ink rounded px-2 py-0.5 tracking-wide">{timecode(at)}</span>
        </div>

        <div className="flex-1 flex min-h-0">
          <div className="w-26 shrink-0 border-r border-deck-line flex flex-col">
            <div className="h-5 border-b border-deck-line" />
            <Head icon="image" name="Video" h={78} />
            <Head icon="type" name="Text" h={30} />
            <Head icon="mic" name="Voice" h={40} />
            <Head icon="music" name="Music" h={36} />
          </div>

          {/* Zoomed in, the lanes are wider than the pane and it scrolls across; the scroll bar is the one along the bottom.
              At 1× everything fits by definition, and the playhead's marker at the very end poked a few pixels past the
              edge, which put a scroll bar there with nothing to scroll to (2026-09-26): so none at 1×. */}
          <div className={`flex-1 min-w-0 ${zoom > 1 ? "overflow-x-auto" : "overflow-x-hidden"} overflow-y-hidden`} data-testid="timeline-scroll">
            <div className="relative h-full" style={{ width: `${zoom * 100}%` }}>
              <Ruler total={total} onSeek={seek} />
              <Lane h={78}>
                {shots.map((s, i) => (
                  <Clip key={s.id} shot={s} total={total} selected={s.id === selId} onClick={() => { setSel(s.id); seek(starts[i] ?? 0); }}>
                    <Poster shot={s} />
                    <span className="absolute inset-x-0 top-0 px-1.5 py-0.5 bg-gradient-to-b from-black/60 to-transparent text-white text-[10px] font-semibold flex">
                      <span className="truncate">{s.position + 1} · {s.type}</span>
                      <span className="ml-auto pl-1 opacity-80">{(s.durationMs / 1000).toFixed(1)}s</span>
                    </span>
                  </Clip>
                ))}
              </Lane>
              <Lane h={30}>
                {shots.map((s, i) => (
                  <Clip key={s.id} shot={s} total={total} selected={s.id === selId} onClick={() => { setSel(s.id); seek(starts[i] ?? 0); }} bare>
                    <span className="h-full px-1.5 flex items-center bg-[#3b3734] text-[10px] text-[#e8e4df] truncate">
                      {s.onScreenText ?? s.dialogue ?? "—"}
                    </span>
                  </Clip>
                ))}
              </Lane>
              <Lane h={40}>
                {shots.map((s, i) => (
                  <Clip key={s.id} shot={s} total={total} selected={s.id === selId} onClick={() => { setSel(s.id); seek(starts[i] ?? 0); }} bare>
                    <span className={`h-full block ${s.assets.voice ? "bg-[#4a2f2a]" : "bg-[#2a2725]"}`} />
                  </Clip>
                ))}
              </Lane>
              <Lane h={36}>
                <span className="h-full w-full block bg-[#2e3a35]" />
              </Lane>
              <div className="absolute top-0 bottom-0 w-0.5 bg-coral pointer-events-none" style={{ left: `${(at / total) * 100}%` }}>
                <span className="absolute -left-1.5 top-0 w-3.5 h-2.5 bg-coral [clip-path:polygon(0_0,100%_0,50%_100%)]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-muted">
        Trimming and reordering are not built yet. A drawn scene's words can be changed; Rerun makes a new version of the selected shot, and is
        charged like the first run.
      </p>
    </div>
  );
}

/**
 * The player's own controls (2026-09-25): the browser's were a strip too small to use on a preview this size, with
 * no way to go shot to shot. Play, back to the start, a shot back and a shot on, a seek bar, the time, the sound, and
 * full screen.
 */
function Transport({ at, total, playing, muted, full, onToggle, onSeek, onStep, onMute, onFull }: {
  at: number; total: number; playing: boolean; muted: boolean; full: boolean;
  onToggle: () => void; onSeek: (s: number) => void; onStep: (dir: 1 | -1) => void; onMute: () => void; onFull: () => void;
}) {
  const b = "p-1.5 rounded-md text-[#e8e4df] hover:bg-white/10";
  return (
    <div className="flex items-center gap-1 px-2 py-1.5 bg-black/35 text-white">
      <button onClick={() => onSeek(0)} title="Back to the start" aria-label="Back to the start" className={b}><Icon name="toStart" size={14} /></button>
      <button onClick={() => onStep(-1)} title="Previous shot" aria-label="Previous shot" className={b}><Icon name="chevronLeft" size={15} /></button>
      <button onClick={onToggle} title={playing ? "Pause (space)" : "Play (space)"} aria-label={playing ? "Pause" : "Play"} className={`${b} bg-white/10`}><Icon name={playing ? "pause" : "play"} size={15} /></button>
      <button onClick={() => onStep(1)} title="Next shot" aria-label="Next shot" className={b}><Icon name="chevronRight" size={15} /></button>
      <input
        type="range" min={0} max={total} step={0.01} value={Math.min(at, total)}
        onChange={(e) => onSeek(Number(e.target.value))}
        aria-label="Position in the video"
        className="flex-1 min-w-0 mx-1.5 accent-coral h-1 cursor-pointer"
      />
      <span className="text-[11px] tabular-nums text-[#c9c3bb] shrink-0">{clock(at)} / {clock(total)}</span>
      <button onClick={onMute} title={muted ? "Sound on (M)" : "Mute (M)"} aria-label={muted ? "Sound on" : "Mute"} aria-pressed={muted} className={b}><Icon name={muted ? "mute" : "volume"} size={15} /></button>
      <button onClick={onFull} title={full ? "Exit full screen (Esc)" : "Full screen"} aria-label={full ? "Exit full screen" : "Full screen"} aria-pressed={full} className={b}><Icon name={full ? "shrink" : "expand"} size={14} /></button>
    </div>
  );
}

const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function Ruler({ total, onSeek }: { total: number; onSeek: (s: number) => void }) {
  // Whole seconds that fall inside the video: rounding up put a "0:25" mark past the end of a 24.4-second one.
  const seconds = Math.floor(total);
  const to = (e: { clientX: number; currentTarget: HTMLElement }) => {
    const r = e.currentTarget.getBoundingClientRect();
    onSeek(Math.max(0, Math.min(total, ((e.clientX - r.left) / r.width) * total)));
  };
  return (
    <div
      className="relative h-5 border-b border-deck-line cursor-col-resize touch-none"
      onClick={to}
      // Drag to scrub: the pointer is held by the ruler until it is let go, so a drag past its end still counts.
      onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); to(e); }}
      onPointerMove={(e) => { if (e.buttons === 1) to(e); }}
    >
      {Array.from({ length: seconds + 1 }, (_, i) => (
        <span key={i} className="absolute top-0 bottom-0 w-px" style={{ left: `${(i / total) * 100}%`, background: i % 5 === 0 ? "#5a534b" : "#3a3634" }}>
          {/* The last mark's label sits inside the ruler, not past its end where it made the lanes scroll. */}
          {i % 5 === 0 ? <span className={`absolute ${i > total - 1.5 ? "right-1" : "left-1"} top-0.5 text-[9.5px] tabular-nums text-[#8a827a]`}>0:{String(i).padStart(2, "0")}</span> : null}
        </span>
      ))}
    </div>
  );
}

function Head({ icon, name, h }: { icon: string; name: string; h: number }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 border-b border-deck-line text-[#8a827a] shrink-0" style={{ height: h }}>
      <Icon name={icon} size={13} />
      <span className="text-[10.5px] font-semibold uppercase tracking-[.04em]">{name}</span>
    </div>
  );
}

function Lane({ h, children }: { h: number; children: React.ReactNode }) {
  return <div className="flex items-center border-b border-deck-line shrink-0" style={{ height: h }}>{children}</div>;
}

function Clip({ shot, total, selected, onClick, bare, children }: {
  shot: ShotView; total: number; selected: boolean; onClick: () => void; bare?: boolean; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative h-[88%] overflow-hidden shrink-0 text-left border-r border-deck ${selected ? "outline-2 outline-lime -outline-offset-2 z-10" : ""} ${bare ? "" : "bg-[#2a2725]"}`}
      style={{ width: `${(shot.durationMs / 1000 / total) * 100}%` }}
    >
      {children}
    </button>
  );
}

function Poster({ shot }: { shot: ShotView }) {
  // A photo piece shows the owner's photo it was made with (2026-09-29); any other scene is its kind.
  const shown = shot.scene?.kind === "showreel" && shot.scene.piece === "photo" ? shot.assets.image?.media : undefined;
  if (shot.scene && !shown) return <span className="h-full w-full grid place-items-center px-1 text-center text-[10px] leading-tight text-[#cfc8bf]">{sceneLabel(shot.scene.kind)}</span>;
  const m = shot.assets.video?.media ?? shot.assets.image?.media;
  if (!m) return <span className="h-full w-full grid place-items-center text-[10px] text-[#8a827a]">{shot.status}</span>;
  // The small copy, a clip's poster frame included: the timeline draws a strip of each, not the whole file (ADR 0051).
  return <img src={m.thumbUrl} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />;
}

/** `plannedMs` is the shot's length in the script, which a clip is bought for, and not the cut's longer one. */
function Inspector({ project, shot, plannedMs }: { project: ProjectView; shot: ShotView | null; plannedMs: number | undefined }) {
  const qc = useQueryClient();
  const [instruction, setInstruction] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const rerun = useMutation({
    mutationFn: () => api.rerun(project.id, shot!.id, instruction || undefined),
    onSuccess: (p) => { qc.setQueryData(["project", project.id], p); setInstruction(""); },
  });
  if (!shot) return <aside className="w-72 shrink-0 text-sm text-muted">Select a clip.</aside>;
  const start = project.shots.slice(0, shot.position).reduce((n, s) => n + s.durationMs, 0) / 1000;
  // What a rerun can redo, as the server has it (`shotRerun`): a drawn scene or a toured photo has no picture to make
  // again, only its voice, and a shot speaks only when its line is the voice's. A line that is not was offered a Rerun
  // the server refused (review, 2026-09-28). Only a picture takes an instruction: a toured photo that speaks took one
  // beside its Rerun and nothing used it (2026-10-12).
  const redo = shotRerun({ ...shot, durationMs: plannedMs ?? shot.durationMs });
  const speaks = redo.voice;
  const redoable = redo.picture || redo.voice;
  return (
    <aside className="w-full md:w-72 shrink-0 flex flex-col gap-2.5 min-h-0">
      <div className="flex items-center gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-[.08em] text-muted">Shot {shot.position + 1} · {shot.type}</h2>
        {shot.assets.image ? <span className="ml-auto px-2 py-0.5 rounded-md bg-ink text-white text-[11px] font-medium">v{shot.assets.image.version}</span> : null}
      </div>
      <div className="bg-panel border border-line rounded-[10px] p-3 text-[12px]">
        <dl className="grid grid-cols-[70px_1fr] gap-x-2.5 gap-y-1.5">
          <dt className="text-muted">In / out</dt><dd className="tabular-nums">{start.toFixed(2)}s → {(start + shot.durationMs / 1000).toFixed(2)}s</dd>
          <dt className="text-muted">Picture</dt><dd>{shot.scene ? `Drawn: ${sceneLabel(shot.scene.kind).toLowerCase()}, “${sceneWords(shot.scene) ?? ""}”` : shot.description}</dd>
          <dt className="text-muted">Says</dt><dd>{shot.dialogue ? `“${shot.dialogue}”` : "—"}</dd>
          <dt className="text-muted">On screen</dt><dd>{shot.onScreenText ?? "—"}</dd>
          <dt className="text-muted">Motion</dt><dd>{shot.motion}</dd>
        </dl>
      </div>
      {shot.scene && editing === shot.id ? (
        <SceneWords project={project} shot={shot as ShotView & { scene: NonNullable<ShotView["scene"]> }} onDone={() => setEditing(null)} />
      ) : shot.scene ? (
        // A drawn scene has no picture to make again; its words are what change it (ADR 0050).
        <>
          <button onClick={() => setEditing(shot.id)} className="self-start px-3 py-1.5 rounded-lg bg-page border border-line text-ink-2 text-xs font-semibold hover:bg-line">Edit words</button>
          <p className="mt-auto text-[11px] text-muted">A drawn scene is made from its words, so there is no picture to make again.{speaks ? " Rerun redoes its voice." : ""}</p>
        </>
      ) : !redo.picture ? (
        <p className="mt-auto text-[11px] text-muted">Your photo is shown as it is, so there is no picture to make again.{speaks ? " Rerun redoes its voice." : ""}</p>
      ) : null}
      {redoable ? <div className={`${redo.picture ? "mt-auto " : ""}flex gap-2`}>
        {redo.picture ? <input
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="change this shot…"
          className="flex-1 min-w-0 border border-line-2 rounded-lg px-2.5 py-1.5 text-xs bg-panel"
        /> : null}
        <button onClick={() => rerun.mutate()} disabled={rerun.isPending || shot.status === "generating"} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-coral text-white text-xs font-semibold disabled:opacity-50 whitespace-nowrap">
          <Icon name="refresh" size={12} /> Rerun · about ${redo.usd.toFixed(2)}
        </button>
      </div> : null}
      {rerun.error ? <p role="alert" className="text-[11px] text-bad">{(rerun.error as Error).message}</p> : null}
    </aside>
  );
}

function timecode(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const f = Math.floor((s % 1) * 30);
  return `00:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}:${String(f).padStart(2, "0")}`;
}
